import { isAxiosError, AxiosError } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'
import { RequestCancelAxios } from 'shared/api/lib'
import { APP_ENV } from 'shared/config'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { PhoneApi } from 'entities/Phone'
import {
  type IParamsCreateNumbersLandline,
  type IResponseCreateNumbersLandlineError,
  type IResponseNumberLandline,
} from 'entities/Phone/api/types'
import { Phone } from 'entities/Phone/model/Phone'
import { type InboxLayerStore } from 'pages/settings/pages/inboxes/pages/id/store/inboxLayerStore'
import { numberLandlineStatus } from 'pages/settings/pages/inboxes/pages/newInbox/store/textEnableLandlineStore'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { InboxesRoutes } from 'pages/settings/pages/inboxes/route/type'
import { AuthorizationDocumentStore } from 'pages/settings/ui/AuthorizationDocumentCommon'
import { EnterAddressStore } from 'pages/settings/ui/EnterAddressCommon'
import { EnterNumberStore } from 'pages/settings/ui/EnterNumberCommon'
import { PaymentCommonStore } from 'pages/settings/ui/PaymentCommon/store/paymentCommonStore'
import { VerifyNumberStore } from 'pages/settings/ui/VerifyNumberCommon'

export enum TextEnableLandlineRoutesSteps {
  EnterNumber = 'enter-number',
  EnterAddress = 'enter-address',
  VerifyNumber = 'verify-number',
  AuthorizationDocument = 'authorization-document',
  Payment = 'payment',
}

const titleMap: Record<string, string> = {
  [TextEnableLandlineRoutesSteps.EnterNumber]: 'Enter number',
  [TextEnableLandlineRoutesSteps.EnterAddress]: 'Enter address',
  [TextEnableLandlineRoutesSteps.VerifyNumber]: 'Verify number',
  [TextEnableLandlineRoutesSteps.AuthorizationDocument]: 'Authorization Document (LOA)',
  [TextEnableLandlineRoutesSteps.Payment]: 'Payment',
}

export class TextEnableLandlineStore {
  private _currentStep = 1
  private _loading = true
  private readonly _enterNumberStore: EnterNumberStore
  private readonly _enterAddressStore: EnterAddressStore
  private readonly _verifyNumberStore: VerifyNumberStore
  private readonly _authorizationDocumentStore: AuthorizationDocumentStore
  private readonly _paymentStore: PaymentCommonStore

  private _phoneQuery: string | null = null
  private _phone: Phone | null = null
  private _cancelRequestLandline = new RequestCancelAxios()
  private _visibleUI = false
  private _inboxNumber = ''

  constructor(private _inboxLayerStore: InboxLayerStore, private _entities: IEntitiesStore) {
    this._enterNumberStore = new EnterNumberStore({
      cancelRequestLandline: this._cancelRequestLandline,
      fetchNumbersLandline: this.fetchNumbersLandline,
    })
    this._enterAddressStore = new EnterAddressStore({
      fetchNumbersLandline: this.fetchNumbersLandline,
      createNumbersLandline: this.createNumbersLandline,
      getPhoneQuery: () => this.phoneQuery,
      nextStepByNumber: this.nextStepByNumber,
      nextStepNumber: 3,
    })
    this._verifyNumberStore = new VerifyNumberStore(
      {
        fetchNumbersLandline: this.fetchNumbersLandline,
        getPhone: () => this.phone,
        getPayload: () => this.payload,
        setPhone: this.setPhone,
        syncData: this.syncData,
        initStep: this.initStep,
      },
      this._entities
    )
    this._authorizationDocumentStore = new AuthorizationDocumentStore(
      {
        getPayload: () => this.payload,
        fetchNumbersLandline: this.fetchNumbersLandline,
        syncData: this.syncData,
        initStep: this.initStep,
        enterNumberStore: this._enterNumberStore,
      },
      this._entities
    )
    this._paymentStore = new PaymentCommonStore(() => {}, this._entities)

    makeAutoObservable(this)
  }

  init = async (query: string) => {
    this._entities.complianceStore.loadNumberVendors()
    await this._entities.subscriptionStore.init()

    const urlSearchParams = new URLSearchParams(query)
    this._phoneQuery = urlSearchParams.get('phone')
    const statusQuery =
      APP_ENV === 'production'
        ? null
        : (urlSearchParams.get('status') as IResponseNumberLandline['status'] | null) // TODO: for testing

    if (this._phoneQuery) {
      this._enterNumberStore.setPhoneDisabled(true)
      this._enterNumberStore.setPhone(this._phoneQuery, false)

      const phoneInternational = this._enterNumberStore.phoneInternational
      const data = await this.fetchNumbersLandline(phoneInternational)

      if ('error' in data) {
        if (data.error === 'No such number') {
          uiStore.changeRouteViaVue({
            path: `/${InboxesPath.inboxNumbers(this.inboxId)}`,
          })
        }

        return
      } else if (numberLandlineStatus.includes(statusQuery)) {
        // TODO: For testing
        data.landline?.updateStatus(statusQuery)
      }

      if (this._phone) {
        const landline = this._phone.landline

        if (!landline || landline?.isCompleted) {
          uiStore.changeRouteViaVue({
            path: `/${InboxesPath.root}`,
          })

          return
        }

        this.syncData(this._phone)
        this.initStep(this._phone)
      }
    }

    runInAction(() => {
      this._loading = false
    })
  }

  syncData = (phone: Phone) => {
    this._phone = phone
    this._enterNumberStore.syncData(phone)
    this._enterAddressStore.syncData(phone)
    this._verifyNumberStore.syncData(phone)
  }

  get inboxLayerStore() {
    return this._inboxLayerStore
  }

  get paymentStore() {
    return this._paymentStore
  }

  get loading() {
    return this._loading
  }

  get inboxId() {
    return this._inboxLayerStore.inboxId
  }

  get hideBack() {
    return this.getCurrentStep === TextEnableLandlineRoutesSteps.VerifyNumber
  }

  get cancelRequestLandline() {
    return this._cancelRequestLandline
  }

  get phone() {
    return this._phone
  }

  get authorizationDocumentStore() {
    return this._authorizationDocumentStore
  }

  get verifyNumberStore() {
    return this._verifyNumberStore
  }

  get enterNumberStore() {
    return this._enterNumberStore
  }

  get enterAddressStore() {
    return this._enterAddressStore
  }

  get payload(): IParamsCreateNumbersLandline {
    return {
      ...this._enterNumberStore.payload,
      ...this._enterAddressStore.payload,
      ...this._verifyNumberStore.payload,
      inbox_number: this._inboxNumber,
      phone_to_replace: this._inboxNumber,
    }
  }

  get phoneQuery() {
    return this._phoneQuery
  }

  setPhoneQuery = (value: string) => {
    this._phoneQuery = value
  }

  get rootPath() {
    return `${InboxesPath.inboxNumbers(this.inboxId)}/${InboxesRoutes.textEnableLandline}`
  }

  get availableLicensesCount() {
    return this._entities.subscriptionStore.licensesCount
  }

  get selectedNumbersCount() {
    return this._phone ? 1 : 0
  }

  get requiredLicensesToBuy() {
    const numbersCount = this.selectedNumbersCount
    const availableLicenses = this.availableLicensesCount

    if (availableLicenses === 0) {
      return numbersCount
    }

    if (availableLicenses < numbersCount) {
      return numbersCount - availableLicenses
    }

    return 0
  }

  get stepMap() {
    return {
      1: TextEnableLandlineRoutesSteps.EnterNumber,
      2: TextEnableLandlineRoutesSteps.EnterAddress,
      3: TextEnableLandlineRoutesSteps.VerifyNumber,
      4: TextEnableLandlineRoutesSteps.AuthorizationDocument,
      5: TextEnableLandlineRoutesSteps.Payment,
    }
  }

  get allStepsLength() {
    return Object.keys(this.stepMap).length
  }

  get getCurrentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)
    if (!step) return this.getCurrentStep
    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)
    if (!step) return this.getCurrentStep
    return step
  }

  getStep = (direction: -1 | 1 | 0) => {
    const allSteps = Object.values(this.stepMap)
    const currentPageIndex = this._currentStep - 1
    return allSteps[currentPageIndex + direction]
  }

  getSettingsStep = (path: TextEnableLandlineRoutesSteps) => {
    return this.getStepByPath(path)
  }

  getStepByPath = (routeStep: TextEnableLandlineRoutesSteps) => {
    const allPaths = Object.entries(this.stepMap)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === routeStep
    })
    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  setVisibleUI = (status: boolean) => {
    this._visibleUI = status
  }

  nextClickHandler = () => {
    uiStore.changeRoute({
      path: `${this.rootPath}/${this.getNextStep}`,
    })
  }

  decreaseStep = () => {
    uiStore.changeRoute({
      path: `${this.rootPath}/${this.getLastStep}`,
      query: uiStore.location?.search,
    })
  }

  redirectToNumbers = async () => {
    uiStore.changeRoute({
      path: InboxesPath.inboxNumbers(this.inboxId),
    })
  }

  setPhone = (phone: Phone) => {
    this._phone = phone
    return this._phone
  }

  nextStepByNumber = (number: number, query?: string) => {
    const step = this.stepMap[number as keyof typeof this.stepMap]

    uiStore.changeRoute({
      path: `${this.rootPath}/${step}`,
      query: query || uiStore.location?.search,
    })
  }

  initStep = (phone: Phone) => {
    const landline = phone?.landline

    if (!landline) return

    if (landline.statusTwilioProcessing) {
      this.nextStepByNumber(3)

      return
    }

    if (landline.hasVerify) {
      this.nextStepByNumber(4)

      return
    }

    if (landline.hasVerified || landline.hasLOA || landline.hasCompleted) {
      this.nextStepByNumber(5)
      this._authorizationDocumentStore.sync()

      return
    }
  }

  fetchNumbersLandline = async (number: string, update = true) => {
    try {
      this._cancelRequestLandline.init()

      const response = await PhoneApi.getNumbersLandline(
        {
          phone: number,
        },
        {
          cancelToken: this._cancelRequestLandline.token,
        }
      )

      const data = response.data

      if ('error' in data) {
        return {
          error: data.error,
        }
      } else {
        const phone = new Phone(data, this._entities)
        if (update) return this.setPhone(phone)
        return phone
      }
    } catch (e) {
      logger.error(e)
      return {
        error: 'unknow',
      }
    }
  }

  createNumbersLandline = async () => {
    try {
      const response = await PhoneApi.createNumbersLandline(this.payload)
      const data = response.data

      if ('error' in data) {
        return {
          error: data.error,
        }
      }

      const phone = new Phone(data, this._entities)
      this.setPhone(phone)
      this.setPhoneQuery(phone.national_number)

      return phone
    } catch (e) {
      const error = e as Error
      logger.error(error)

      if (isAxiosError<IResponseCreateNumbersLandlineError>(error)) {
        if (!error.response) {
          return {
            error: error.message,
          }
        }
        return {
          error: {
            ...error.response.data,
          },
        }
      }
      return {
        error: error.message,
      }
    }
  }

  buyNumber = async () => {
    this.paymentStore.setPaymentError(false)
    this._inboxNumber = ''
    if (!this.inboxId) return

    try {
      runInAction(() => {
        this._loading = true
        this._inboxNumber = this._inboxLayerStore.currentTeam?.number?.number || ''
      })

      const response = await PhoneApi.createNumbersLandlineInboxUpdate(this.payload)
      const data = response.data

      if ('error' in data) {
        logger.error(data.error)
        toastStore.add({
          type: 'error',
          title: 'Landline is not added',
          desc: 'This landline number couldn’t be added to the inbox',
        })
        if (this._visibleUI) {
          this.redirectToNumbers()
        }

        return
      } else {
        toastStore.add({
          type: 'success',
          title: 'Landline is in progress to be text-enabled',
          desc: 'Sign the LOA to complete the process',
        })

        if (this._visibleUI) {
          this.redirectToNumbers()
        }
      }
    } catch (e) {
      logger.error(e)
      toastStore.add({
        type: 'error',
        title: 'Landline is not added',
        desc: 'This landline number couldn’t be added to the inbox',
      })

      if (e instanceof AxiosError) {
        if (e.errors.getError('stripe') === 'Your card was declined.') {
          this.paymentStore.setPaymentError(true)
        }
      }
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._enterNumberStore.dispose()
    this._enterNumberStore.setPhone('')
    this._phone = null
    this._cancelRequestLandline.reset()
    this._enterAddressStore.dispose()
  }
}
